//@ts-nocheck
import {
  applySnapshot,
  cast,
  flow,
  getEnv,
  getParent,
  getSnapshot,
  types,
} from "mobx-state-tree";
import pluralize from "pluralize";
import { DiscountCoupon } from "./search-context";
import { TutorResponseStore } from "./tutor";

type DataProps = {
  stateFx: number;
  vicinityFx: number;
  extraStudentDiscount: number;
  splitRequest: Array<{
    purposeFx: number;
    baseRate: number;
    hourFx: number;
    lessonDays: number;
    lessonHours: number;
    lessonDuration: number;
    students: number;
    subjectsFx: number;
    curriculumFx: number;
    name: string;
  }>;
  standardFx: number;
  premiumFx: number;
  deluxeFx: number;
};

export const determinePricingPlan = (data: DataProps, factor = 1) => {
  const locationFx = data.stateFx * data.vicinityFx;
  const extraStudentFx = data.extraStudentDiscount / 100;
  const lessonDuration = data.splitRequest[0].lessonDuration;

  const priceReducer = (accumulator, current) => {
    const scheduleValue =
      current.lessonDays *
      current.lessonHours *
      current.lessonDuration *
      current.hourFx;
    const learningFx =
      current.purposeFx * current.curriculumFx * current.subjectsFx;
    const hourlyRate = current.baseRate * locationFx * learningFx;
    const singleStudentPrice = hourlyRate * scheduleValue;
    const additionalStudents = current.students - 1;
    const totalStudentsPrice =
      singleStudentPrice * (1 + additionalStudents * extraStudentFx);
    return accumulator + Math.ceil(totalStudentsPrice / 100) * 100;
  };

  const lessonReducer = (accumulator, current) => {
    return accumulator + current.lessonDays;
  };

  const totalPrice = data.splitRequest.reduce(priceReducer, 0);
  const totalLessons = data.splitRequest.reduce(lessonReducer, 0);
  return {
    lessonPayments: data.splitRequest.map((o) => {
      let lessonFee = priceReducer(0, o) * factor;
      return {
        name: o.name,
        lessons: lessonReducer(0, o) * lessonDuration,
        distance: 0,
        students: 1,
        lessonFee,
        lessonRate:
          lessonFee / (o.lessonDays * o.lessonDuration * o.lessonHours),
        extraStudent: 0,
        perStudentFee: lessonFee,
        transportFare: 0,
        firstBookingDiscount: 0,
      };
    }),
    duration:
      lessonDuration === 4
        ? "per month"
        : `for ${pluralize("week", lessonDuration, true)}`,
    lessons: lessonDuration * totalLessons,
    standardPrice: totalPrice * data.standardFx,
    premiumPrice: totalPrice * data.premiumFx,
    deluxePrice: totalPrice * data.deluxeFx,
  };
};

const SubjectFactor = types.model("SubjectFactor", {
  subject: types.string,
  rate: types.number,
});
const VicinityFactor = types.model("VicinityFactor", {
  state: types.string,
  vicinity: types.string,
  vicinity_factor: types.number,
});
const PurposeFactor = types.model("PurposeFactor", {
  purpose: types.string,
  factor: types.number,
});
const CurriculumFactor = types.model("CurriculumFactor", {
  curriculum: types.string,
  factor: types.number,
});
const PlanFactor = types.model("PlanFactor", {
  plan_type: types.string,
  factor: types.number,
});
const SubjectCounterFactor = types.model("SubjectCounterFactor", {
  subject_count: types.string,
  factor: types.number,
});
const StateFactor = types.model("StateFactor", {
  state: types.string,
  factor: types.number,
});

const SocialClassFactor = types.model("SocialClassFactor", {
  social_class: types.string,
  factor: types.number,
});
const HourFactor = types.model("HourFactor", {
  hours: types.string,
  factor: types.number,
});
const PricingStore = types
  .model("PricingStore", {
    subjectFactor: types.optional(types.array(SubjectFactor), []),
    vicinityFactor: types.optional(types.array(VicinityFactor), []),
    purposeFactor: types.optional(types.array(PurposeFactor), []),
    curriculumFactor: types.optional(types.array(CurriculumFactor), []),
    planFactor: types.optional(types.array(PlanFactor), []),
    subjectCounterFactor: types.optional(types.array(SubjectCounterFactor), []),
    stateFactor: types.optional(types.array(StateFactor), []),
    socialClassFactor: types.optional(types.array(SocialClassFactor), []),
    hourFactor: types.optional(types.array(HourFactor), []),
  })
  .views((self) => {
    const getPlanFactor = (kind: string) => {
      let result = self.planFactor.find((o) =>
        o.plan_type.toLowerCase().includes(kind.toLowerCase()),
      );
      return result ? result.factor : 1;
    };
    return {
      get standardFx() {
        return getPlanFactor("standard");
      },
      get premiumFx() {
        return getPlanFactor("premium");
      },
      get deluxeFx() {
        return getPlanFactor("deluxe");
      },
      get extraStudentDiscount() {
        return getPlanFactor("extrastudent");
      },
      getStateFactor(state: string) {
        let found = self.stateFactor.find(
          (o) => o.state.toLowerCase() == state.toLowerCase(),
        );
        console.log(`state ${found?.factor} ${state}`);
        return found ? found.factor : 1;
      },
      getVicinityFactor(vicinity: string, state: string) {
        let found = self.vicinityFactor.find(
          (o) =>
            o.state.toLowerCase() == state.toLowerCase() &&
            o.vicinity.toLowerCase() == vicinity.toLowerCase(),
        );
        console.log(`vicinity ${found?.vicinity_factor} ${vicinity} ${state}`);
        return found ? found.vicinity_factor : 1;
      },
      getSubjectRate(subject: string) {
        let home_tutoring = self.subjectFactor.find((u) =>
          u.subject.toLowerCase().includes("home tutoring"),
        );
        let found = self.subjectFactor.find(
          (u) => u.subject.toLowerCase() == subject.toLowerCase(),
        );
        return found ? found.rate : home_tutoring ? home_tutoring.rate : 1;
      },
      getCurriculumFactor(curriculums: string[]) {
        let found = self.curriculumFactor
          .filter((u) =>
            curriculums
              .map((j) => j.toLowerCase())
              .includes(u.curriculum.toLowerCase()),
          )
          .map((o) => o.factor);
        console.log(`curriculum ${found} ${curriculums}`);
        return found.length > 0 ? Math.max(...found) : 1;
      },
      getPurposeFactor(purposes: string[]) {
        let found = self.purposeFactor
          .filter((u) =>
            purposes
              .map((j) => j.toLowerCase())
              .includes(u.purpose.toLowerCase()),
          )
          .map((o) => o.factor);
        console.log(`purpose ${found} ${purposes}`);
        return found.length > 0 ? Math.max(...found) : 1;
      },
      getSubjectsFactor(subjects: string[]) {
        let options = {};
        self.subjectCounterFactor.forEach((k) => {
          options[k.subject_count] = k.factor;
        });
        if (subjects.length < 3) {
          return options["Less than 3"] || 1;
        }
        if (subjects.length >= 3 || subjects.length <= 4) {
          return options["3 or 4"] || 1;
        }
        if (subjects.length >= 5) {
          return options["5 & above"] || 1;
        }
        return 1;
      },
      getHourFactor(hour = 1) {
        let options = {};
        self.hourFactor.forEach((k) => {
          options[k.hours] = k.factor;
        });
        if (hour <= 1) {
          return options["1 hour"] || 1;
        }
        if (hour > 1 && hour <= 1.5) {
          return options["1.5 hour"] || 1;
        }
        if (hour >= 2 && hour <= 3) {
          return options["2-3 hours"] || 1;
        }
        if (hour <= 4) {
          return options["4 hours"] || 1;
        }
        return options["5 hours"];
      },
    };
  });

export const ClientRequestStore:any = types
  .model("ClientRequestStore", {
    responses: types.optional(types.array(TutorResponseStore), []),
    currency: types.optional(types.string, "₦"),
    isLoading: types.optional(types.boolean, false),
    pricingStore: types.optional(PricingStore, {}),
    discountObj: types.maybe(DiscountCoupon),
    useDiscount: types.optional(types.boolean, true),
  })
  .views((self) => {
    return {
      get requestData() {
        let parent: any = getParent(self, 1);
        return parent.requestData;
        // let currentSplitRequest = getSnapshot(self.tutors);
        // return {
        //   ...self.request.requestData,
        //   splitRequests: self.request.getScheduleSplit(currentSplitRequest),
        // };
      },
      generatePaymentInfo(plan: string) {
        let parent: any = getParent(self, 1);
        let { splitRequests: tutors } = parent;
        let { standardFx, premiumFx, deluxeFx } = self.pricingStore;
        let options = {
          standard: standardFx,
          premium: premiumFx,
          deluxe: deluxeFx,
        };
        let factor = options[plan.toLowerCase()];
        let {
          lessonPayments = [],
          standardPrice,
          premiumPrice,
          deluxePrice,
          lessons: totalLessons,
        } = this.buildPaymentInfo(factor);
        let priceOptions = {
          standard: standardPrice,
          premium: premiumPrice,
          deluxe: deluxePrice,
        };
        let tuitionFee = priceOptions[plan.toLowerCase()];
        let { lessonSchedule, lessonType }: any =
          this.requestData.lessonDetails;
        let delivery = lessonType;
        let lessonHours = lessonSchedule ? lessonSchedule.lessonHours : "";
        let daysRequested = lessonSchedule ? lessonSchedule.lessonDays : [];
        let daysSelected = [];
        tutors
          .filter((split) => split.names.length > 0)
          .map((split) => daysSelected.push(...split.lessonDays));
        daysSelected = [...new Set([...daysSelected])];
        let totalDiscount = 0;
        let couponDiscount = 0;
        let transportFare = 0;
        if (self.discountObj) {
          couponDiscount = self.discountObj.totalDiscount(tuitionFee);
          totalDiscount += couponDiscount;
        }
        let appliedDiscount = totalDiscount > 0;
        if (!self.useDiscount) {
          totalDiscount = 0;
          couponDiscount = 0;
        }
        let totalTuition = tuitionFee + transportFare - totalDiscount;
        let isBillableDistance = transportFare > 0 && delivery === "physical";
        let activeTeacherIDs = tutors
          .filter((split) => split.names.length > 0)
          .map((teacher) => teacher.selectedTutor?.userId)
          .filter((o) => o);
        return {
          discountCode: self.discountObj?.discountCode || "",
          timeSubmitted: new Date().toISOString(),
          couponDiscount,
          daysRequested: daysRequested,
          hoursOfLesson: lessonHours,
          lessonPayments,
          totalTuition,
          tuitionFee,
          totalDiscount,
          transportFare,
          planSelected: plan,
          daysSelected,
          isBillableDistance,
          appliedDiscount,
          totalLessons,
          activeTeacherIDs,
        };
      },

      get info() {
        let parent: any = getParent(self, 1);

        let {
          useRequestDataProps: request,
          bookingInfo,
          splitRequests: tutors,
        } = parent;
        let { serverInfo } = request;
        return {
          created: serverInfo.created,
          modified: serverInfo.modified,
          status: serverInfo.status,
          agent: serverInfo.agent ? getSnapshot(serverInfo.agent) : null,
          lessonDetails: {
            lessonType: request.requestData.lessonDetails.lessonType,
            ...request.requestData.lessonDetails.lessonSchedule,
          },
          slug: request.slug,
          childDetails: request.requestData.childDetails,
          contactDetails: request.requestData.contactDetails,
          paymentInfo: bookingInfo
            ? {
                amountPaid:
                  bookingInfo.actualAmountPaid > 0
                    ? bookingInfo.actualAmountPaid
                    : bookingInfo.totalTuition,
                totalLessons: bookingInfo.totalLessons,
                totalTuition: bookingInfo.totalTuition,
                speakingFee: bookingInfo.speakingFee,
                monthsPaid: bookingInfo.monthsPaid,
                totalDiscount: bookingInfo.totalDiscount,
                timeSubmitted: bookingInfo.timeSubmitted,
              }
            : {},
          tutorDetails: tutors.map((tutor) => {
            let tutorInfo = tutor.tutorData;
            let response = self.responses.find(
              (o) => o.tutor_slug === tutor.tutorId,
            );
            return {
              names: tutor.names,
              tutorId: tutor.tutorId,
              lessonDays: tutor.lessonDays,
              subjectGroup: tutor.subjectGroup,
              searchSubject: tutor.searchSubject,
              tutorInfo: {
                firstName: tutorInfo.firstName,
                level: tutorInfo.level,
                photo: tutorInfo.photo,
                vicinity: tutorInfo.vicinity,
                region: tutorInfo.region,
                rating: tutorInfo.rating,
                ratingCount: tutorInfo.ratingCount,
              },
              tutorResponse: {
                status: response?.status,
                dateSubmitted: response?.dateSubmitted,
              },
            };
          }),
        };
      },
      get name() {
        /**
          replace sample data with
          correct implementation
        */
        return "receipt";
      },
      get pricingInfo() {
        let parent: any = getParent(self, 1);
        let {
          useRequestDataProps: request,
          splitRequests: tutors,
          requestData: {
            lessonDetails: { lessonSchedule },
          },
        } = parent;
        let {
          lessonLocationStore: { state, vicinity },
        } = request;
        let { standardFx, premiumFx, deluxeFx, extraStudentDiscount } =
          self.pricingStore;
        let splitRequest = tutors.map((j) => {
          return {
            name: (Array.isArray(j.names) ? j.names[0] : j.names) || "",
            purposeFx: self.pricingStore.getPurposeFactor(j.purposes),
            baseRate: self.pricingStore.getSubjectRate("home tutoring"),
            hourFx: self.pricingStore.getHourFactor(j.lessonHours),
            lessonDays: j.lessonDays.length,
            lessonHours: j.lessonHours || lessonSchedule.lessonHours,
            lessonDuration: j.lessonDuration || lessonSchedule.lessonDuration,
            students: j.names.length,
            subjectsFx: self.pricingStore.getSubjectsFactor(j.subjectGroup),
            curriculumFx: self.pricingStore.getCurriculumFactor(j.curriculum),
          };
        });
        return {
          standardFx,
          premiumFx,
          deluxeFx,
          extraStudentDiscount,
          splitRequest,
          stateFx: self.pricingStore.getStateFactor(state),
          vicinityFx: self.pricingStore.getVicinityFactor(vicinity, state),
        };
      },
      buildPaymentInfo(factor = 1) {
        return determinePricingPlan(this.pricingInfo as any, factor);
      },
    };
  })
  .actions((self) => {
    function mapToStore({ tutorResponses = [], pricingInfo = {} }) {
      // let tutors = splitRequests.map((splitRequest) => {
      //   return {
      //     ...splitRequest,
      //     tutorData: splitRequest.tutorId
      //       ? tutorsData.find((o) => o.userId === splitRequest.tutorId)
      //       : null,
      //   };
      // });
      // .filter((o) => o.tutorId);

      // self.request = RequestFlowStore.create({
      //   lessonScheduleStore: { ...lessonDetails.lessonSchedule },
      //   lessonLocationStore: {
      //     lessonType: lessonDetails.lessonSchedule.lessonType,
      //     country: contactDetails.country,
      //     state: contactDetails.state,
      //     region: contactDetails.region || "",
      //     vicinity: contactDetails.vicinity || "",
      //     address: contactDetails.address || "",
      //     country_code: contactDetails.country_code,
      //     customerType: contactDetails.customerType,
      //   },
      //   contactInfoStore: contactDetails,
      //   slug,
      //   childStore: {
      //     childDetails: transformInput(childDetails).childDetails,
      //   },
      //   bookingInfo:
      //     tutors.length > 0
      //       ? { ...paymentInfo, splitRequests: tutors }
      //       : undefined,
      //   serverInfo: {
      //     created,
      //     status,
      //     agent,
      //     modified,
      //   },
      // });
      // self.request.mapToStore(requestData);
      // self.tutors = cast(tutors);
      self.responses = cast(tutorResponses);
      applySnapshot(self.pricingStore, pricingInfo);
    }

    const generatePdfLink = (data, name) => {
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${name}.pdf`);
      document.body.appendChild(link);
      link.click();
    };

    const downloadReceipt = flow(function* downloadReceipt() {
      let { adapter } = getEnv(self);
      let parent: any = getParent(self, 1);
      try {
        self.isLoading = true;
        const result = yield adapter.downloadReceipt(parent.slug);
        generatePdfLink(result, self.name);
        self.isLoading = false;
        return result;
      } catch (error) {
        self.isLoading = false;
        throw error;
      }
    });
    const onPlanSelected = flow(function* onPlanSelected(plan) {
      let { adapter } = getEnv(self);
      try {
        self.isLoading = true;
        let data = self.requestData;
        let paymentInfo = self.generatePaymentInfo(plan);
        const result = yield adapter.savePricingInfo(data, paymentInfo);
        return result;
      } catch (error) {
        console.log(error);
        self.isLoading = false;
        throw error;
      }
    });
    return {
      mapToStore,
      downloadReceipt,
      onPlanSelected,
    };
  });
